import * as merge from 'deepmerge'

import {
  formatYear
} from '../../../shared/utils'
import {
  getFormatterForVarName,
} from '../app/selectors'
import { getLang } from '../app/selectors/lang'
import { getDataSubset } from '../../timeseries/utils'

/** Default options for timeseries container */
const gridOptions = {
  top: '24',
  right: '24',
  bottom: '24',
  left: '24'
}

/** Default options for x axis */
const xAxisOptions = {
  type: 'value',
  nameTextStyle: {
    fontSize: 14,
    fontWeight: 'bold'
  },
  nameLocation: 'center',
  nameGap: 16,
  splitLine: {
    show: false
  },
  axisLine: {
    show: false,
    onZero: 0
  },
  axisTick: {
    show: false,
  }
}

/** Default options for y axis */
const yAxisOptions = {
  type: 'value',
  splitLine: {
    show: false,
    lineStyle: {
      type: 'dashed',
      color: ['rgba(0,0,0,0.1)']
    }
  },
  axisTick: {
    show: false
  },
  axisLine: {
    show: false
  }
}

/** Default options for visual map */
const visualMapOptions = {
  dimension: 1,
  calculable: false,
  show: false
}

const formatYValue = (varName, val) => {
  const formatter = getFormatterForVarName(varName)
  return formatter(val)
}

/**
 * Merge axis overrides with default axis options
 * @param {string} axisName 'x' or 'y'
 * @param {object} overrides https://ecomfe.github.io/echarts-doc/public/en/option.html#xAxis
 */
const getAxisOptions = (axisName, overrides = {}) =>
  merge(
    axisName === 'x' ? xAxisOptions : yAxisOptions,
    overrides
  )

/**
 * Merge container overrides with default container options
 * @param {object} overrides https://ecomfe.github.io/echarts-doc/public/en/option.html#grid
 */
const getGridOptions = (overrides = {}) =>
  merge(gridOptions, overrides)

/**
 * Merge visual map overrides with default visual map options
 * @param {object} overrides https://ecomfe.github.io/echarts-doc/public/en/option.html#visualMap
 */
const getVisualMapOptions = (overrides = []) =>
  overrides.map(vm => merge(visualMapOptions, vm))

/**
 * Gets the base timeseries config with the provided overrides
 * @param {*} overrides any override options for the timeseries
 */
const extendTimeseriesStyle = (overrides = {}) => ({
  ...overrides,
  grid: getGridOptions(overrides.grid),
  xAxis: getAxisOptions('x', overrides.xAxis),
  yAxis: getAxisOptions('y', overrides.yAxis),
  visualMap: getVisualMapOptions(overrides.visualMap)
})

const isDataReady = ({ data }) => data && data.length !== 0

/** GRID CONFIGURATION  */

export const grid = () => {
  return {
    top: 5,
    // right: 0,
    // bottom: getCSSVariable('--sp-bottom'),
    bottom: 40,
    // left: getCSSVariable('--sp-left'),
    left: 40,
  }
}

/** TIMESERIES OVERLAY CONFIGURATION */


/** X AXIS CONFIGURATION */

const getXAxis = ({ extent, varName, ...rest }) => {
  const [min, max] = extent
  return {
    min,
    max,
    interval: varName === "abs" ? 2 : 3,
    // inverse: region === 'schools',
    axisLabel: {
      // show: false
      // rotate: 30,
      margin: 8,
      fontSize: 10,
      formatter: formatYear
    },
    axisTick: {
      show: true,
      lineStyle: { type: 'solid', color: '#e4e4e4' }
    },
    splitLine: {
      show: true,
      interval: 1,
      lineStyle: { type: 'solid', color: '#e4e4e4' }
    },
    ...rest
  }
}

const xAxis = ({ varName, region, extent }) =>
  getXAxis({
    varName,
    region,
    extent
  })

/** Y AXIS CONFIGURATION */

const getYAxis = ({ varName, extent, ...rest }) => {
  const [min, max] = extent
  const formatter = value => formatYValue(varName, value)
  return {
    min,
    max,
    position: 'left',
    // offset: -5,
    axisLabel: {
      // rotate: 30,
      // margin: 4,
      fontSize: 10,
      formatter: formatter
    },
    axisTick: {
      show: true,
      lineStyle: { type: 'solid', color: '#e4e4e4' }
    },
    splitLine: {
      show: true,
      lineStyle: { type: 'solid', color: '#e4e4e4' }
    },
    nameLocation: 'middle',
    ...rest
  }
}

const yAxis = ({ varName, region, extent }) =>
  getYAxis({ varName, region, extent })

/**
 * Gets the echart options for the main chart, with visual map, dot sizing, grid lines, axis labels, etc.
 * @param {*} variant
 * @param {*} param1
 * @param {*} base
 * @returns
 */
export const getOverlayOptions = (
  {
    data = [],
    trendData = [],
    xExtentsMissing = [],
    xVar,
    yVar
  },
  base
) => {

  const noDataImgSrc = `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAYAAAAGCAYAAADgzO9IAAAACXBIWXMAAALFAAACxQGJ1n/vAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAABSSURBVHgBXY2xDcAwCAQNk6ShYIxMFmeyjIEQRTIJMUhGskwD/D1PU9XfzK42S0SO0F6M+XP3XpCZcz8hHYj4hJa9E9GdBqjzHcKavULcs+vnAD0LMsH4zZo3AAAAAElFTkSuQmCC`
  const noDataImg = new Image()
  noDataImg.src = noDataImgSrc

  const baseSeriesData = isDataReady({ data })
    ? getDataSubset(data, [xVar, yVar]).filter(d => d[1] !== null && !Number.isNaN(d[1]))
    : []

  const trendSeriesData = trendData.map(({ id, data }) => ({
    id,
    data: getDataSubset(data, [xVar, yVar])
  }))

  const beforeCovidData = baseSeriesData.filter(d => d[0] <= xExtentsMissing[0])
  const duringCovidData = baseSeriesData.filter(d => d[0] == xExtentsMissing[0] || d[0] == xExtentsMissing[1])
  const afterCovidData = baseSeriesData.filter(d => d[0] >= xExtentsMissing[1])

  const gradeOptionsSeries = [
    ...trendSeriesData.map(({ id, data }) => ({
      z: 2,
      id,
      data,
      type: 'line',
      symbol: false,
      symbolSize: false,
      lineStyle: {
        width: 2,
        type: 'dotted',
        color: '#4986E8',
      },
    })),
    {
      z: 1,
      id: 'before-covid',
      type: 'line',
      data: beforeCovidData,
      symbol: 'circle',
      symbolSize: 8,
      itemStyle: {
        color: '#60A0DC',
        borderColor: '#0071DB',
        borderWidth: 2
      },
      lineStyle: {
        color: '#A1C4E4',
      },
    },
    {
      z: 1,
      id: 'during-covid',
      type: 'line',
      data: duringCovidData,
      symbol: 'circle',
      symbolSize: 0,
      lineStyle: {
        color: 'transparent'
        // color: '#A1C4E4',
        // type: 'dotted'
      },
      markArea: {
        silent: true,
        itemStyle: {
          opacity: 0.4,
          color: {
            image: noDataImg,
            repeat: 'repeat',
          },
        },
        label: {
          color: '#757575',
          position: 'bottom',
          distance: 24
        },
        data: [
          [
            // TODO: Pass this in from SedaTimeseries
            { xAxis: xExtentsMissing[0] + 0.5, name: 'No Data', },
            { xAxis: xExtentsMissing[1] - 0.5 }
          ]
        ]
      }
    },
    {
      z: 1,
      id: 'after-covid',
      type: 'line',
      data: afterCovidData,
      symbol: 'circle',
      symbolSize: 8,
      itemStyle: {
        color: '#60A0DC',
        borderColor: '#0071DB',
        borderWidth: 2
      },
      lineStyle: {
        color: '#A1C4E4',
      },
    },
  ]
  const options = {
    ...base,
    // series: metric === 'abs' ? absOptionsSeries : gradeOptionsSeries
    series: gradeOptionsSeries
  }
  return extendTimeseriesStyle(options)
}

/**
 * Returns an eCharts options object
 * @param {*} variant
 * @param {*} param1
 * @returns
 */
export const getTimeseriesOptions = (context = {}) => {
  if (!context.data || context.data.length === 0) return {}
  const { extents, metric, theme } = context

  const baseOptions = {
    grid: grid(),
    xAxis: xAxis({
      // varName: xVar,
      varName: metric,
      extent: extents[0],
      zLevel: 10,
    }),
    yAxis: yAxis({
      // varName: yVar,
      varName: metric,
      extent: extents[1],
      zLevel: 10
    }),
    tooltip: {
      show: true,
      trigger: 'item',
      confine: true,
      className: 'timeseries__tooltip',
      padding: 10,
      backgroundColor: theme.app.darkBackground,
      textStyle: {
        color: theme.palette.common.white,
        fontFamily: 'inherit',
      },
      extraCssText: `border-radius: ${theme.shape.borderRadius}px`,
      formatter: params => {
        return `
          <div class="timeseries__tooltip--content">
            <div class="timeseries__tooltip--year">
              ${params.data[0]}
            </div>
            <div class="timeseries__tooltip--value">
              <div>${getLang(`LABEL_SINGULAR_${metric}`)}</div>
              <div>${formatYValue(metric, params.data[1])}</div>
            </div>
            <div class="timeseries__tooltip--unit">
              ${getLang('HINT_TIMESERIES')}
            </div>
          </div>
        `
      }
    },
  }
  const overlayOptions = getOverlayOptions(
    context,
    baseOptions
  )
  return overlayOptions
}
